import React, { useCallback, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import styles from "./EvaluateArea.module.css";
import layout from "../../../Modules/layout.module.css";
import SectionsDrawer from "../../../Components/SectionsDrawer/SectionsDrawer";
import { useMediaQuery } from "../../../Hooks/useMediaQuery";
import QuestionSectionCriteria from "../../../Components/Products/QuestionSectionCriteria";
import { IDrawerItem } from "../../../Types/Drawer/ISectionsDrawerItem";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Store";
import {
  getQuestionnaireByAreaId,
  selectDrawerSections,
  selectRolePermissions,
  UpdateCriteriaDate,
  updateQuestionnaireScope,
  updateQuestionnaireTitle,
} from "../../../Redux/Store/Slices/questionnaire";
import CreateDevelopImprovement from "../../Develop/DevelopArea/CreateImprovement/CreateDevelopImprovement";
import FloatingLabelTextArea from "../../../Components/Utils/Form/FloatingLabelTextArea/FloatingLabelTextArea";
import LoadingWrapper from "../../../Components/Utils/Loading/LoadingWrapper";
import ContextMenu from "../../../Components/Utils/ContextMenu/ContextMenu";
import { ContextMenuItem } from "../../../Types/ContextMenuItem";
import Popup from "../../../Components/Utils/Popup/Popup";
import InviteRemoveUsers from "../../../Components/Utils/InviteRemoveUsers/InviteRemoveUsers";
import ChangeOwnerPopup from "../../../Components/Utils/ChangeOwnerPopup/ChangeOwnerPopup";
import Tooltip from "../../../Components/Utils/Tooltip/Tooltip";
import { UNSET_DATE } from "../../../Helpers/Dates";
import AdditionalInformation from "./AdditionalInformation/AdditionalInformation";
import EditableTextField from "../../../Components/Utils/EditableTextField/EditableTextField";
import ExportButton from "../../../Components/Utils/ExportButton/ExportButton";
import KpiAreaSummaryModal from "../../../Components/KPI/KpiAreaSummaryModal";
import { getClient } from "../../../Redux/Store/Slices/client";
import { hasKpiEditPermission } from "../../../Helpers/kpiPermissions";
import { useClientKpiAccess } from "../../../Hooks/useClientKpiAccess";
import { getProductTypeName } from "../../../Types/ProductType";
import { useUnsavedChangesWarning } from "../../../Hooks/useUnsavedChangesWarning";

const MOBILE_BREAKPOINT = "(max-width: 960px)";

const EvaluateArea = () => {
  const [showInviteUsersPopup, setShowInviteUsersPopup] = useState<boolean>(false);
  const [showChangeOwnerPopup, setShowChangeOwnerPopup] = useState<boolean>(false);
  const [manageAreaContextMenuData, setManageAreaContextMenuData] = useState<ContextMenuItem[]>([]);
  const [localScope, setLocalScope] = useState<string>('');
  const [scopeReady, setScopeReady] = useState(false);
  const [sectionsDrawerOpen, setSectionsDrawerOpen] = useState(false);
  const [kpiSummaryOpen, setKpiSummaryOpen] = useState(false);
  const [kpiInitialKeyResultId, setKpiInitialKeyResultId] = useState<string | null>(null);
  const [kpiRefreshToken, setKpiRefreshToken] = useState(0);
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const { areaId, sectionId, criteriaId } = useParams();
  const location = useLocation();
  const basePath = `/evaluate/${areaId}`;
  const currentId = areaId;
  const isOnCreateImprovementRoute = location.pathname.includes('/create-improvement');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const questionnaire = useAppSelector((state) => state.questionnaire.questionnaire);
  const areaKpiSummary = useAppSelector((state) => state.questionnaire.areaKpiSummary);
  const rolePermissions = useAppSelector(selectRolePermissions());
  const drawerSections = useAppSelector(selectDrawerSections);
  const client = useAppSelector(getClient);
  const { canAccessClientKpi } = useClientKpiAccess();

  const openKpiSummary = useCallback((keyResultId?: string) => {
    if (!canAccessClientKpi) return;
    setKpiInitialKeyResultId(keyResultId ?? null);
    setKpiSummaryOpen(true);
  }, [canAccessClientKpi]);

  const closeKpiSummary = useCallback(() => {
    setKpiSummaryOpen(false);
    setKpiInitialKeyResultId(null);
    setKpiRefreshToken((t) => t + 1);
    if (currentId) {
      dispatch(getQuestionnaireByAreaId({ areaId: currentId, force: true }));
    }
  }, [currentId, dispatch]);

  useEffect(() => {
    if (currentId) {
      dispatch(getQuestionnaireByAreaId(currentId));
    }
  }, [currentId, dispatch]);

  // Sync local scope with questionnaire scope when it changes
  useEffect(() => {
    if (questionnaire?.scope !== undefined) {
      setLocalScope(questionnaire.scope ?? '');
      setScopeReady(true);
    }
  }, [questionnaire?.scope]);

  useUnsavedChangesWarning(scopeReady && localScope !== (questionnaire?.scope ?? ''));

  useEffect(() => {
    if (!questionnaire?.questionnaireSections?.length || !drawerSections?.length) {
      return;
    }

    // Find the first real questionnaire section (skip intro/action items that have no criteria)
    const firstDrawerSection = drawerSections.find(s => s.items && s.items.length > 0 && !s.isIntro && !s.isAction);
    const firstDrawerCriteria = firstDrawerSection?.items?.[0];
    
    if (!firstDrawerSection || !firstDrawerCriteria) {
      return;
    }

    // Check if current params are valid for this area
    const hasValidParams = sectionId && criteriaId && 
      questionnaire.questionnaireSections.some(section => 
        section.id === sectionId && 
        section.criteria.some(criteria => criteria.id === criteriaId)
      );

    const isOnScopeRoute = location.pathname.includes('/scope');
    const isOnAdditionalInfoRoute = location.pathname.includes('/additional-info');

    if (!hasValidParams && !isOnScopeRoute && !isOnAdditionalInfoRoute && !isOnCreateImprovementRoute) {
      navigate(`/evaluate/${currentId}/${firstDrawerSection.id}/${firstDrawerCriteria.id}`);
    }
  }, [questionnaire, drawerSections, currentId, sectionId, criteriaId, navigate, location.pathname, isOnCreateImprovementRoute]);

  useEffect(() => {
    setManageAreaContextMenuData([
      {
        title: "View Users",
        icon: "/img/plus-minus.svg",
        disabled: false,
        onClickHandler: () => {
          setShowInviteUsersPopup(true);
        }
      },
      {
        title: 'Change Owner',
        icon: "/img/change.svg",
        disabled: !rolePermissions?.changeOwner,
        disabledTooltip: `Your role as ${rolePermissions?.roleTitle} means you cannot change the owner`,
        onClickHandler: () => {
          setShowChangeOwnerPopup(true);
        },
      },
    ]);
  }, [currentId, rolePermissions]);

  const handleCompletionChanged = async (item: IDrawerItem, checked: boolean) => {

     console.log("handleCompletionChanged", { id: item.id, checked, at: Date.now() });
  console.trace("trace handleCompletionChanged");
  
    const section = questionnaire?.questionnaireSections
      .find(s => s.criteria.some(c => c.id === item.id));
    const criteria = section?.criteria.find(c => c.id === item.id);
    
    if (criteria && section && areaId) {
      await dispatch(UpdateCriteriaDate({
        criteriaId: criteria.id,
        sectionId: section.id,
        activityId: areaId,
        date: checked ? new Date() : UNSET_DATE
      }));
    }
  };

  if (drawerSections && questionnaire?.id) {
    return (
      <LoadingWrapper loading={false}>
        <div className={layout.column}>
          <div className={styles.pageContainer}>
            <div className={`${layout.row2} ${styles.contentRow}`}>
              <div className={styles.left}>
                <div className={styles.titleContainer}>
                  <div className={styles.titleField}>
                    <EditableTextField
                      value={questionnaire?.title || ''}
                      onChange={(newTitle) => dispatch(updateQuestionnaireTitle(newTitle))}
                      canEdit={rolePermissions?.editTitle}
                      disabledTooltip={`Your role as a ${rolePermissions?.roleTitle} means you cannot edit the title`}
                      disabledTooltipAlign="bottom"
                      placeholder="Untitled Area"
                      className={styles.title}
                    />
                  </div>
                  <div className={styles.titleActions}>
                    {isMobile && (
                      <button
                        type="button"
                        className={styles.sectionsDrawerOpenButton}
                        onClick={() => setSectionsDrawerOpen(true)}
                        aria-label="Open sections"
                      >
                        <img src="/img/menu-meatballs.svg" alt="" aria-hidden width={18} height={18} />
                        Sections
                      </button>
                    )}
                    <div className={styles.documentActionsGroup} aria-label="Document actions">
                      {currentId && (
                        <>
                          <div className={styles.documentActionExport}>
                            <Tooltip
                              text={`Your role as a ${rolePermissions?.roleTitle} means you cannot export this diagnostic`}
                              align="top"
                              sideAlign="left"
                              disabled={rolePermissions?.exportDiagnostic}
                            >
                              <ExportButton
                                areaIds={currentId}
                                label="Export"
                                size={null}
                                embedded
                                disabled={!rolePermissions?.exportDiagnostic}
                                productName={
                                  questionnaire?.productType !== undefined
                                    ? getProductTypeName(questionnaire.productType)
                                    : "Evaluate"
                                }
                              />
                            </Tooltip>
                          </div>
                          <span className={styles.documentActionsDivider} aria-hidden />
                        </>
                      )}
                      <div className={styles.documentActionGear}>
                        <ContextMenu 
                          items={manageAreaContextMenuData} 
                          contextId="" 
                          position="left"
                        >
                          <img
                            className={`${layout.icon} ${styles.gear}`}
                            src="/img/gear.svg"
                            alt="Manage area"
                          />
                        </ContextMenu>
                      </div>
                    </div>
                  </div>
                  <Popup isVisible={showInviteUsersPopup} onClose={() => setShowInviteUsersPopup(false)}>
                    <InviteRemoveUsers productType={questionnaire?.productType} />
                  </Popup>
                  {currentId && (
                    <Popup isVisible={showChangeOwnerPopup} onClose={() => setShowChangeOwnerPopup(false)}>
                      <ChangeOwnerPopup
                        areaId={currentId}
                        canChangeOwner={rolePermissions?.changeOwner}
                        onOwnerChanged={() => {
                          if (currentId) {
                            dispatch(getQuestionnaireByAreaId({ areaId: currentId, force: true }));
                          }
                        }}
                        onClose={() => setShowChangeOwnerPopup(false)}
                      />
                    </Popup>
                  )}
                  <Popup
                    isVisible={kpiSummaryOpen}
                    onClose={closeKpiSummary}
                    allowSnapToSide
                  >
                    {currentId && client?.id ? (
                      <KpiAreaSummaryModal
                        active={kpiSummaryOpen}
                        areaId={currentId}
                        areaType="Evaluate"
                        prefetchedSummary={areaKpiSummary}
                        clientId={client.id}
                        initialKeyResultId={kpiInitialKeyResultId}
                        hasKpiEdit={hasKpiEditPermission(rolePermissions)}
                        roleTitle={rolePermissions?.roleTitle}
                        onClose={closeKpiSummary}
                        areaBadgeLabel={questionnaire?.title ?? undefined}
                      />
                    ) : null}
                  </Popup>
                </div>
                <Routes>
                  <Route path=":sectionId/:itemId/*" element={<QuestionSectionCriteria />} />
                  <Route path="create-improvement" element={<CreateDevelopImprovement />} />
                  <Route 
                    path="scope" 
                    element={
                      <Tooltip 
                        text={`Your role as a ${rolePermissions?.roleTitle} means you cannot edit the scope`} 
                        align="top" 
                        disabled={rolePermissions?.editScope}
                      >
                        <div className={styles.scopeContainer}>
                          <FloatingLabelTextArea
                            readonly={!rolePermissions?.editScope}
                            label="Introduction"
                            onChange={(value) => setLocalScope(value)}
                            onBlur={() => dispatch(updateQuestionnaireScope({ 
                              areaId: currentId!, 
                              scope: localScope 
                            }))}
                            value={localScope}
                          />
                        </div>
                      </Tooltip>
                    }
                  />
                  <Route path="additional-info" element={<AdditionalInformation />} />
                </Routes>
              </div>

              {!isMobile && (
                <div className={styles.right}>
                  <SectionsDrawer
                    sections={drawerSections}
                    basePath={basePath}
                    activeSectionId={sectionId ?? (window.location.pathname.includes('/create-improvement') ? "create-improvement" : window.location.pathname.includes('/additional-info') ? "additional-info" : "scope")}
                    activeItemId={criteriaId}
                    onCheckBoxChanged={handleCompletionChanged}
                    diagnosticKpi={
                      currentId
                        ? {
                            areaId: currentId,
                            areaType: "Evaluate" as const,
                            refreshToken: kpiRefreshToken,
                            prefetchedSummary: areaKpiSummary,
                            hasKpiEdit: hasKpiEditPermission(rolePermissions),
                            onOpenKpiSummary: openKpiSummary,
                          }
                        : undefined
                    }
                  />
                </div>
              )}
            </div>

            {isMobile && (
              <>
                <div
                  className={`${styles.sectionsDrawerBackdrop} ${sectionsDrawerOpen ? styles.sectionsDrawerBackdropOpen : ''}`}
                  onClick={() => setSectionsDrawerOpen(false)}
                  aria-hidden
                />
                <div
                  className={`${styles.sectionsDrawerOverlay} ${sectionsDrawerOpen ? styles.sectionsDrawerOverlayOpen : ''}`}
                  role="dialog"
                  aria-label="Sections"
                  aria-modal="true"
                >
                  <div className={styles.sectionsDrawerOverlayHeader}>
                    <h2 className={styles.sectionsDrawerOverlayTitle}>Sections</h2>
                    <button
                      type="button"
                      className={styles.sectionsDrawerOverlayClose}
                      onClick={() => setSectionsDrawerOpen(false)}
                      aria-label="Close sections"
                    >
                      ×
                    </button>
                  </div>
                  <div className={styles.sectionsDrawerOverlayContent}>
                    <SectionsDrawer
                      sections={drawerSections}
                      basePath={basePath}
                      activeSectionId={sectionId ?? (window.location.pathname.includes('/create-improvement') ? "create-improvement" : window.location.pathname.includes('/additional-info') ? "additional-info" : "scope")}
                      activeItemId={criteriaId}
                      onCheckBoxChanged={handleCompletionChanged}
                      onNavigate={() => setSectionsDrawerOpen(false)}
                      diagnosticKpi={
                        currentId
                          ? {
                              areaId: currentId,
                              areaType: "Evaluate" as const,
                              refreshToken: kpiRefreshToken,
                              prefetchedSummary: areaKpiSummary,
                              hasKpiEdit: hasKpiEditPermission(rolePermissions),
                              onOpenKpiSummary: openKpiSummary,
                            }
                          : undefined
                      }
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </LoadingWrapper>
    );
  }

  return (
    <LoadingWrapper loading={true}>
      <div />
    </LoadingWrapper>
  );
};

export default EvaluateArea;
